// Pure helpers for the Knowledge Filer admin page — kept out of the component so
// the decisions (what a night's status means, how cost reads, how a write is
// labelled) are unit-tested as plain functions.
import type { FilerRunSummary, FilerWrite } from '../services/knowledgeFiler';

export type BadgeVariant = 'success' | 'warning' | 'error' | 'neutral';

export function runStatusVariant(status: string): BadgeVariant {
  if (status === 'succeeded') return 'success';
  if (status === 'running') return 'warning';
  if (status === 'failed') return 'error';
  return 'neutral';
}

/** A night with nothing offered is a quiet night, not a broken one. */
export function runOutcomeLabel(run: Pick<FilerRunSummary, 'status' | 'offered' | 'writes_ok' | 'error'>): string {
  if (run.status === 'running') return 'running';
  if (run.status === 'failed') return run.error ? `failed: ${run.error}` : 'failed';
  if (run.offered === 0) return 'nothing new';
  return `${run.writes_ok} write${run.writes_ok === 1 ? '' : 's'} landed`;
}

export function formatCost(cost: number | null | undefined): string {
  if (cost === null || cost === undefined) return '—';
  return `$${cost.toFixed(2)}`;
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

const BANK_LABELS: Record<string, string> = {
  kb_business: 'Business',
  kb_signals: 'Quality Signals',
  kb_kirk_copilot: 'Technical',
  kb_chemmasters_dev: 'Agent-Memory',
  kb_investigation: 'Investigation',
  kb_resolution: 'Resolution',
};

export function bankLabel(serverId: string): string {
  return BANK_LABELS[serverId] ?? serverId;
}

const TOOL_LABELS: Record<string, string> = {
  report_learning: 'new entry',
  edit_kb_entry: 'edit',
  update_signal_entry: 'update',
  upsert_playbook: 'playbook',
  delete_kb_entry: 'delete',
  delete_playbook: 'delete playbook',
};

export function toolLabel(tool: string): string {
  return TOOL_LABELS[tool] ?? tool;
}

export function writeVariant(write: Pick<FilerWrite, 'ok'>): BadgeVariant {
  if (write.ok === true) return 'success';
  if (write.ok === false) return 'error';
  return 'neutral';
}

/** Writes grouped by bank, landed vs not, for the per-run summary line. */
export function writesByBank(writes: FilerWrite[]): Array<{ bank: string; landed: number; failed: number }> {
  const acc = new Map<string, { landed: number; failed: number }>();
  for (const w of writes) {
    const bank = bankLabel(w.server_id);
    const row = acc.get(bank) ?? { landed: 0, failed: 0 };
    if (w.ok === true) row.landed += 1;
    else if (w.ok === false) row.failed += 1;
    acc.set(bank, row);
  }
  return [...acc.entries()]
    .map(([bank, v]) => ({ bank, ...v }))
    .sort((a, b) => b.landed - a.landed || a.bank.localeCompare(b.bank));
}

export function consoleLink(conversationId: string | null): string | null {
  return conversationId ? `/agent-console?conversation=${encodeURIComponent(conversationId)}` : null;
}
